import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.petlings.gg/api/v1';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // send httpOnly cookies automatically
});

// Fallback: also attach token from localStorage for mod/token-link flows
// The cookie is the primary auth mechanism, this is backup
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('petlings_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auth
export const login = (email, password) =>
  api.post('/auth/login', { email, password });

export const register = (email, password, verificationCode) =>
  api.post('/auth/register', { email, password, verification_code: verificationCode });
export const addCredentials = (email, password, verificationCode) =>
  api.post('/auth/add-credentials', { email, password, verification_code: verificationCode });

export const getMe = () => api.get('/auth/me');
// Deletes the account and everything held for it. A Sign in with Apple
// account answers 409 until an Apple confirmation is included.
export const deleteAccount = (apple = null) =>
  api.post('/auth/account/delete', {
    confirm: 'DELETE',
    apple_identity_token: apple?.identityToken ?? null,
    apple_authorization_code: apple?.authorizationCode ?? null,
    apple_nonce: apple?.nonce ?? null,
  });
// Detaches the linked Minecraft account; the person keeps everything. Needs
// the password, or for a Sign in with Apple account an Apple confirmation
// (409 until one is included). Answers with a new login: every other ends.
export const unlinkMinecraft = ({ password = null, apple = null } = {}) =>
  api.post('/auth/minecraft/unlink', {
    confirm: 'UNLINK',
    password,
    apple_identity_token: apple?.identityToken ?? null,
    apple_nonce: apple?.nonce ?? null,
  });
export const appleLogin = (identityToken, nonce, fullName) =>
  api.post('/auth/apple', { identity_token: identityToken, nonce, full_name: fullName });
export const logoutApi = () => api.post('/auth/logout');
export const sendCode = (email, purpose = 'reset') =>
  api.post('/auth/send-code', { email, purpose });
export const verifyCode = (email, code, newPassword = null) =>
  api.post('/auth/verify-code', { email, code, new_password: newPassword });

// Pets & Sync
export const getUserData = () => api.get('/petlings/sync');
export const saveUserData = (data) => api.put('/petlings/sync', data);
export const getPets = () => api.get('/pets/pets');
export const getPet = (slot) => api.get(`/pets/pets/${slot}`);
export const updatePet = (slot, pet) => api.put(`/pets/pets/${slot}`, pet);
export const releasePet = (slot) => api.delete(`/pets/pets/${slot}`);
export const adoptPet = (petTypeId, petName, traitIds = [], variantId = null, tiny = false) =>
  api.post('/pets/pets/adopt', { pet_type_id: petTypeId, pet_name: petName, trait_ids: traitIds, variant_id: variantId, tiny });
export const getCatalog = () => api.get('/pets/catalog');
export const getFullCatalog = () => api.get('/catalog/all');
export const getOwnership = () => api.get('/pets/ownership');
export const getProfile = () => api.get('/pets/profile');

// Cosmetics
export const getCosmeticsCatalog = () => api.get('/cosmetics/catalog');
export const getOwnedCosmetics = () => api.get('/cosmetics/owned');

// Chat
export const sendChatMessage = (message, petName, petType, petSlot = 0, mood = 'happy') =>
  api.post('/chat', { message, pet_name: petName, pet_type: petType, pet_slot: petSlot, mood });
export const getChatHistory = (petSlot = 0, limit = 50) =>
  api.get('/chat/history', { params: { pet_slot: petSlot, limit } });

// Shop & Economy
export const getWallet = () => api.get('/shop/wallet');
export const getTreatBundles = () => api.get('/shop/bundles');
export const getShopItems = (category) => api.get('/shop/items', { params: category ? { category } : {} });
export const purchaseItem = (itemId, quantity = 1) => api.post('/shop/purchase', { item_id: itemId, quantity });
export const useItem = (itemId, petSlot = 0) => api.post('/shop/use', { item_id: itemId, pet_slot: petSlot });
export const getInventory = () => api.get('/shop/inventory');
export const getUnlocks = () => api.get('/shop/unlocks');
export const getTransactions = (limit = 20) => api.get('/shop/transactions', { params: { limit } });
export const getSubscriptions = () => api.get('/shop/subscriptions');
export const getSlotPrices = () => api.get('/shop/slot-prices');
export const unlockSlot = () => api.post('/shop/unlock-slot');

// Interactions (with cooldowns)
export const interact = (action, petSlot = 0, foodId = null) =>
  api.post('/interact', { action, pet_slot: petSlot, food_id: foodId });

// Account Linking
export const generateLinkCode = () => api.post('/auth/link/generate');

// Health
export const getHealth = () => api.get('/health');

export default api;
